import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Mic, MicOff, Loader2, MessageSquare, Volume2, Sparkles } from "lucide-react";

/**
 * NoorLive — Full-screen "live conversational avatar" experience (Phase 3 DEMO).
 * Uses existing STT + Chat + TTS to simulate a real-time interactive avatar.
 * When a proper LiveAvatar/WebRTC key is added, only the audio playback layer
 * needs to be swapped with a real streaming session — the UX stays identical.
 */
export default function NoorLive({ lang, API, open, onClose, onOpenChat }) {
    const isAr = lang === "ar";
    const [sessionId] = useState(() => `live-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [userTranscript, setUserTranscript] = useState("");
    const [noorReply, setNoorReply] = useState(
        isAr
            ? "أهلاً بك! اضغط على الميكروفون وتحدث معي مباشرة."
            : "שלום! לחץ על המיקרופון ותדבר איתי ישירות."
    );
    const [phase, setPhase] = useState("idle"); // idle | listening | thinking | speaking

    const videoRef = useRef(null);
    const audioRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioChunksRef = useRef([]);

    // Autoplay looping video (muted) when opened
    useEffect(() => {
        if (!open) return;
        setPhase("idle");
        setUserTranscript("");
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play().catch(() => {});
        }
        return () => {
            // Cleanup on close
            try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
            try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
            try { mediaRecorderRef.current?.stop(); } catch (_e) { /* ignore */ }
            mediaStreamRef.current = null;
        };
    }, [open]);

    // Sync video with phase to avoid lip-sync mismatch:
    // While Noor is speaking (audio from HeyGen TTS is playing) the pre-recorded video
    // clip would show wrong mouth movements. So we freeze the video on a neutral frame
    // during "speaking" and only let it loop when idle / listening.
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (phase === "speaking") {
            // Snap to a calm neutral frame (~1s in usually looks natural)
            try {
                v.pause();
                v.currentTime = 1.0;
            } catch (_e) { /* ignore */ }
        } else {
            // idle / listening / thinking — let the ambient loop play
            try {
                if (v.paused) v.play().catch(() => {});
            } catch (_e) { /* ignore */ }
        }
    }, [phase]);

    const startRecording = async () => {
        if (isRecording || isProcessing || isSpeaking) return;
        try {
            // Ensure video is playing (user gesture unlocks autoplay)
            try { videoRef.current?.play?.(); } catch (_e) { /* ignore */ }
            // Stop any current Noor audio (interrupt speaking)
            try { audioRef.current?.pause(); } catch (_e) { /* ignore */ }
            setIsSpeaking(false);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
            let mimeType = "";
            for (const m of mimeCandidates) {
                if (!m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))) { mimeType = m; break; }
            }
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
            mr.onstop = async () => {
                const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
                try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
                mediaStreamRef.current = null;
                if (blob.size < 1000) { setPhase("idle"); return; }
                await handleTurn(blob);
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setIsRecording(true);
            setPhase("listening");
        } catch (_e) {
            setNoorReply(isAr
                ? "لم أستطع الوصول إلى الميكروفون. الرجاء منح الإذن."
                : "לא הצלחתי לגשת למיקרופון. אנא אפשר גישה.");
            setPhase("idle");
        }
    };

    const stopRecording = () => {
        if (!isRecording) return;
        try { mediaRecorderRef.current?.stop(); } catch (_e) { /* ignore */ }
        setIsRecording(false);
    };

    const handleTurn = async (blob) => {
        setIsProcessing(true);
        setPhase("thinking");
        try {
            // 1) STT
            const fd = new FormData();
            fd.append("file", blob, "recording.webm");
            fd.append("language", lang);
            const sttR = await axios.post(`${API}/noor/stt`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const userText = (sttR.data?.text || "").trim();
            if (!userText) {
                setNoorReply(isAr ? "لم أسمعك جيداً. حاول مرة أخرى." : "לא שמעתי אותך היטב. נסה שוב.");
                setPhase("idle");
                return;
            }
            setUserTranscript(userText);

            // 2) Chat (Claude)
            const chatR = await axios.post(`${API}/bot/chat`, { sessionId, message: userText, language: lang });
            const reply = chatR.data?.reply || "";
            setNoorReply(reply);

            // 3) TTS (Noor's cloned voice)
            setPhase("speaking");
            setIsSpeaking(true);
            const ttsR = await axios.post(`${API}/noor/tts`, { text: reply, language: lang, speed: 0.95 });
            const audioUrl = ttsR.data?.audio_url;
            if (!audioUrl) { setIsSpeaking(false); setPhase("idle"); return; }
            const audio = new Audio(audioUrl);
            audioRef.current = audio;
            audio.onended = () => { setIsSpeaking(false); setPhase("idle"); };
            audio.onerror = () => { setIsSpeaking(false); setPhase("idle"); };
            audio.play().catch(() => { setIsSpeaking(false); setPhase("idle"); });
        } catch (_e) {
            setNoorReply(isAr ? "حدث خطأ. حاول مرة أخرى." : "אירעה שגיאה. נסה שוב.");
            setPhase("idle");
        } finally {
            setIsProcessing(false);
        }
    };

    const phaseLabel = () => {
        if (phase === "listening") return isAr ? "🔴 نور تسمعك..." : "🔴 נור מקשיבה...";
        if (phase === "thinking") return isAr ? "💭 نور تفكر..." : "💭 נור חושבת...";
        if (phase === "speaking") return isAr ? "🔊 نور تتحدث..." : "🔊 נור מדברת...";
        return isAr ? "اضغط للتحدث" : "לחץ כדי לדבר";
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-gradient-to-br from-[#0B1F3D] via-[#152845] to-[#0B1F3D] flex flex-col"
            data-testid="noor-live-modal"
        >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 md:px-8 md:py-4 border-b border-white/10 backdrop-blur">
                <div className="flex items-center gap-3">
                    <span className="relative flex items-center gap-1.5 bg-red-600/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE
                    </span>
                    <div className="text-white">
                        <div className="text-sm md:text-base font-bold flex items-center gap-1.5">
                            {isAr ? "محادثة مباشرة مع نور" : "שיחה חיה עם נור"}
                            <span className="text-[9px] bg-[#D4AF37] text-[#0B1F3D] px-1.5 py-0.5 rounded font-black">BETA</span>
                        </div>
                        <div className="text-[10px] md:text-xs text-[#D4AF37] flex items-center gap-1">
                            <Sparkles size={10} />
                            {isAr ? "Whisper · Claude · HeyGen Voice" : "Whisper · Claude · HeyGen Voice"}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onOpenChat}
                        className="text-white/80 hover:text-white text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 hover:border-white/40 transition"
                        title={isAr ? "التبديل إلى الدردشة النصية" : "עבור לצ'אט טקסט"}
                        data-testid="noor-live-open-chat"
                    >
                        <MessageSquare size={13} />
                        <span className="hidden md:inline">{isAr ? "دردشة نصية" : "צ'אט טקסט"}</span>
                    </button>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition"
                        data-testid="noor-live-close"
                        aria-label="close"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Main stage */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 md:py-10 overflow-y-auto">
                {/* Video circle */}
                <div className="relative">
                    {/* Glowing rings while listening/speaking */}
                    {(phase === "listening" || phase === "speaking") && (
                        <>
                            <span className="absolute inset-0 rounded-full border-[3px] border-[#D4AF37]/50 animate-ping" style={{ animationDuration: "2s" }} />
                            <span className="absolute inset-0 rounded-full border-[3px] border-[#D4761F]/40 animate-ping" style={{ animationDuration: "2.8s", animationDelay: "0.4s" }} />
                        </>
                    )}
                    <div className={`relative rounded-full overflow-hidden border-[6px] shadow-[0_25px_80px_rgba(212,175,55,0.4)] w-[280px] h-[280px] md:w-[400px] md:h-[400px] transition-all duration-500 ${
                        phase === "speaking" ? "border-[#D4AF37] scale-105" :
                        phase === "listening" ? "border-red-500" :
                        phase === "thinking" ? "border-[#D4761F]" :
                        "border-white/20"
                    }`}>
                        <video
                            ref={videoRef}
                            src="/noor-demo-v3.mp4"
                            muted
                            loop
                            playsInline
                            preload="auto"
                            className="w-full h-full object-cover"
                        />
                        {/* Speaking overlay — soft golden veil + audio-wave bars.
                             Hides the fact that pre-recorded lips are frozen. */}
                        {phase === "speaking" && (
                            <>
                                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#D4AF37]/25 pointer-events-none" />
                                <div className="absolute bottom-5 left-0 right-0 flex items-end justify-center gap-1 h-10 pointer-events-none">
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                                        <span
                                            key={i}
                                            className="w-1 md:w-1.5 rounded-full bg-[#D4AF37]"
                                            style={{
                                                animation: `noor-wave 0.9s ease-in-out ${i * 0.08}s infinite alternate`,
                                                height: "20%",
                                            }}
                                        />
                                    ))}
                                </div>
                                <style>{`@keyframes noor-wave { 0% { height: 15%; opacity: 0.7 } 100% { height: 95%; opacity: 1 } }`}</style>
                            </>
                        )}
                        {/* Overlay while thinking */}
                        {phase === "thinking" && (
                            <div className="absolute inset-0 bg-[#0B1F3D]/40 flex items-center justify-center backdrop-blur-sm">
                                <Loader2 size={64} className="text-[#D4AF37] animate-spin" />
                            </div>
                        )}
                    </div>
                    {/* Phase label */}
                    <div className="mt-4 text-center">
                        <div className="inline-block bg-white/10 text-white text-sm md:text-base font-semibold px-4 py-1.5 rounded-full backdrop-blur">
                            {phaseLabel()}
                        </div>
                    </div>
                </div>

                {/* Subtitles */}
                <div className="w-full max-w-2xl mt-6 md:mt-8 space-y-3">
                    {userTranscript && (
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-3 md:p-4 backdrop-blur">
                            <div className="text-[10px] text-[#D4AF37] font-bold uppercase mb-1">
                                {isAr ? "أنت" : "אתה"}
                            </div>
                            <div className="text-white/90 text-sm md:text-base leading-relaxed">
                                {userTranscript}
                            </div>
                        </div>
                    )}
                    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl p-3 md:p-4 backdrop-blur">
                        <div className="text-[10px] text-[#D4AF37] font-bold uppercase mb-1 flex items-center gap-1">
                            <Volume2 size={11} />
                            {isAr ? "نور" : "נור"}
                        </div>
                        <div className="text-white text-sm md:text-base leading-relaxed whitespace-pre-wrap">
                            {noorReply}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom mic bar */}
            <div className="border-t border-white/10 backdrop-blur px-4 py-4 md:py-6 flex flex-col items-center gap-2">
                <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-white shadow-[0_10px_40px_rgba(212,175,55,0.3)] transition-all duration-300 ${
                        isRecording
                            ? "bg-red-600 hover:bg-red-700 scale-110 animate-pulse"
                            : isProcessing
                                ? "bg-[#D4761F] opacity-60 cursor-wait"
                                : "bg-gradient-to-br from-[#D4761F] to-[#D4AF37] hover:scale-110"
                    }`}
                    data-testid="noor-live-mic"
                    aria-label={isRecording ? "stop" : "start recording"}
                >
                    {isProcessing
                        ? <Loader2 size={30} className="animate-spin" />
                        : isRecording
                            ? <MicOff size={30} />
                            : <Mic size={30} />}
                </button>
                <div className="text-[10px] md:text-xs text-white/50">
                    {isRecording
                        ? (isAr ? "اضغط لإيقاف التسجيل" : "לחץ לעצירת ההקלטה")
                        : (isAr ? "اضغط وتحدث بحرية بالعبرية أو العربية" : "לחץ ודבר בחופשיות בעברית או ערבית")}
                </div>
            </div>
        </div>
    );
}
